import React, { useCallback, useEffect, useMemo } from 'react'
import MenuLine from './MenuLine';
import { getAnchorOffset, insertImage } from '../RichEditor/utils';
import { initializeBoxContent } from '../../utils/boxes';
import { useStore } from '../../store/store';
import { useTranslation } from 'react-i18next';
import { buildImageVariableOptions } from '../Image/imageVariableOptions';
import { getSectionRecursiveMeta } from '../../utils/recursiveSections';

const delimiter = "/"

const imageCommandKey = "img"

const normalize = (value) =>
  (value ?? '')
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const selector = ({
  gridConfiguration,
  configuration,
  defaultVariablesApi,
  currentDocument,
  currentTemplate,
  recursiveModelFields,
  sections,
  boxes,
}) => ({
  gridConfiguration,
  configuration,
  defaultVariablesApi,
  currentDocument,
  currentTemplate,
  recursiveModelFields,
  sections,
  boxes,
});

const SelectionCommandsTranslator = ({
  options,
  selectedOptionKey,
  setSelectedOptionKey,
  setEditorState,
  startOffset,
  endOffset,
  blockText,
  setOptions,
  menuAncestorType,
  menuAncestorIds,
  defaultSelection,
  setDefaultSelection
}) => {
  const {
    gridConfiguration,
    configuration,
    defaultVariablesApi,
    currentDocument,
    currentTemplate,
    recursiveModelFields,
    sections,
    boxes,
  } = useStore(selector);
  const { t } = useTranslation();

  // Modèle récursif du template courant, ou de la section d'où provient le
  // texte en cours d'édition (même logique que ImageCascader).
  const recursiveModelName = useMemo(() => {
    const templateMeta = getSectionRecursiveMeta(currentTemplate);
    if (templateMeta.isRecursive && templateMeta.recursiveModelName) {
      return templateMeta.recursiveModelName;
    }
    // menuAncestorIds contient des ids de box : on remonte à leur section
    // d'origine pour savoir si l'on édite dans une section récursive.
    const boxIds = Array.isArray(menuAncestorIds) ? menuAncestorIds : [];
    for (const boxId of boxIds) {
      const ancestorBox = boxes?.find((b) => String(b.id) === String(boxId));
      if (!ancestorBox?.fromSectionId) continue;
      const section = sections?.find(
        (s) => String(s.id) === String(ancestorBox.fromSectionId)
      );
      const meta = getSectionRecursiveMeta(section);
      if (meta.isRecursive) return meta.recursiveModelName;
    }
    return null;
  }, [currentTemplate, sections, boxes, menuAncestorIds]);

  const imageVariables = useMemo(
    () =>
      buildImageVariableOptions({
        defaultVariablesApi,
        currentDocument,
        recursiveModelName,
        recursiveModelFields,
        t,
      }),
    [defaultVariablesApi, currentDocument, recursiveModelName, recursiveModelFields, t]
  );

  const imageVariablesByKey = useMemo(() => {
    return imageVariables.reduce((acc, variable) => {
      acc[variable.key] = variable;
      return acc;
    }, {});
  }, [imageVariables]);

  const formatCommandsAsOptions = useCallback(() => {
    if (endOffset !== startOffset) {
      return [];
    }

    let startVariableName;
    for (
      startVariableName = startOffset - 1;
      startVariableName > 0 && blockText[startVariableName] !== delimiter;
      startVariableName -= 1);
    if (blockText[startVariableName] !== delimiter ||
      (startVariableName !== 0 && blockText[startVariableName - 1].match(/^[0-9a-z]+$/i))) {
      return []
    }

    const searchInput = blockText.slice(startVariableName + 1, startOffset)
    const searchTokens = normalize(searchInput).split(/\s+/).filter(Boolean)

    const allOptions = [
      { key: imageCommandKey, name: "Image", isRecursive: false },
      ...imageVariables.map(({ key, name, isRecursive }) => ({ key, name, isRecursive })),
    ]

    if (searchTokens.length === 0) return allOptions

    // Tous les mots saisis doivent être présents dans le libellé.
    return allOptions.filter(({ name }) => {
      const normalizedName = normalize(name)
      return searchTokens.every((token) => normalizedName.includes(token))
    })
  }, [blockText, endOffset, startOffset, imageVariables])

  useEffect(() => {
    setOptions(formatCommandsAsOptions())
  }, [formatCommandsAsOptions, setOptions])

  const addImgVariable = useCallback((imageVariable) => {
    const box = initializeBoxContent({
      box: { ...gridConfiguration['img'], type: 'img', width: 100, height: 'auto' },
      configuration,
    });
    if (imageVariable) {
      // Même pose de valeurs que ImageCascader (getNewImageBoxFromAdmin).
      box.content = {
        ...box.content,
        src: imageVariable.src,
        adminValue: imageVariable.adminValue,
        ratio: null,
      };
      box.height = 'auto';
    }
    setEditorState((editorState) => insertImage({
      blockKey: editorState.getSelection().getStartKey(),
      editorState,
      startOffset,
      anchorOffset: getAnchorOffset({ editorState, startOffset, delimiter }),
      box,
      menuAncestorType,
      menuAncestorIds
    },
    ))
  }, [gridConfiguration, configuration, setEditorState, startOffset, menuAncestorType, menuAncestorIds])

  const onSelect = useCallback((event, optionKey) => {
    event.stopPropagation()
    event.preventDefault()
    if (optionKey === imageCommandKey) {
      addImgVariable()
    } else if (imageVariablesByKey[optionKey]) {
      addImgVariable(imageVariablesByKey[optionKey])
    }
  }, [addImgVariable, imageVariablesByKey])

  const handleSelectAction = useCallback((event) => {
    if (!['Enter'].includes(event.key) || !options || options?.length < 1) return;

    onSelect(event, selectedOptionKey)
  }, [onSelect, options, selectedOptionKey])

  useEffect(() => {
    window.addEventListener('keydown', handleSelectAction, true);
    return () => {
      window.removeEventListener('keydown', handleSelectAction, true);
    };
  }, [handleSelectAction]);

  useEffect(() => {
    if (defaultSelection === "img") {
      addImgVariable()
      setDefaultSelection(null)
    }
  }, [addImgVariable, configuration, defaultSelection, gridConfiguration, menuAncestorIds, menuAncestorType, setEditorState, startOffset])


  return (
    <>
      {options.map(({ key, name, isRecursive }) =>
        <MenuLine
          key={key}
          optionKey={key}
          optionName={name}
          selectedOptionKey={selectedOptionKey}
          onSelect={onSelect}
          setSelectedOptionKey={setSelectedOptionKey}
          isRecursive={isRecursive}
        />
      )}
    </>
  )
}

export default SelectionCommandsTranslator;
